import React, { useState } from 'react';
import T from 'prop-types';
import { useTranslation } from 'react-i18next';
import { PageHeader, useResolutionCheck } from 'web-components';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

import GatewayTable from '../../components/GatewayTable';
import { ContainerHeader, ContentWrapper } from '../../elements';
import { Box, Button } from '@mui/material';
import UpdateGatewaysModal from './UpdateGatewaysModal';
import GatewayCrudDialog from './GatewayCrudDialog';
import GatewayDeleteDialog from './GatewayDeleteDialog';
import { GATEWAY_DEFAULT_LIST_ORDER, GATEWAY_DEFAULT_LIST_SORT } from '../../attrs/gateways';
import { PAGINATION_DEFAULT_ROWS_PER_PAGE_MAIN, PAGINATION_DEFAULT_START_PAGE } from '../../attrs/pagination';

const initialGatewayListQueryState = {
  serialNumber: null,
  configured: null,
  organizationName: null,
  connection: null,
  limit: PAGINATION_DEFAULT_ROWS_PER_PAGE_MAIN,
  page: PAGINATION_DEFAULT_START_PAGE,
  order: GATEWAY_DEFAULT_LIST_ORDER,
  sort: GATEWAY_DEFAULT_LIST_SORT
};

const Gateways = ({ gateways, reloadAction }) => {
  const { t } = useTranslation();
  const { isMobile, isDesktop } = useResolutionCheck();
  const [updateGatewaysModalOpen, setUpdateGatewaysModalOpen] = useState(false);
  const [createGatewayModalOpen, setCreateGatewayModalOpen] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [deletingGateway, setDeletingGateway] = useState(null);
  const [gatewayListQueryState, setGatewayListQueryState] = useState(initialGatewayListQueryState);

  const handleCloseModal = () => setUpdateGatewaysModalOpen(false);
  const handleCloseCreateGateway = () => setCreateGatewayModalOpen(false);
  const handleCloseEditGateway = () => setEditingGateway(null);
  const handleCloseDeleteGateway = () => setDeletingGateway(null);
  const handleDeletedGateway = () => {
    handleCloseDeleteGateway();
    reloadAction(gatewayListQueryState);
  };

  return (
    <>
      <ContainerHeader className="withoutShadow">
        <PageHeader.Title>
          <PageHeader.Title.Main>{t('gateways.title')}</PageHeader.Title.Main>
          <PageHeader.Title.Sub>{t('gateways.subtitle')}</PageHeader.Title.Sub>
        </PageHeader.Title>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            sx={{ p: 1.5, gap: 1, display: 'flex', height: '55px', minWidth: '190px' }}
            startIcon={<AddOutlinedIcon />}
            onClick={() => setCreateGatewayModalOpen(true)}
            data-test-id="add-new-gateway-button"
          >
            {t('gateways.crud.create.open_button')}
          </Button>
          <Button
            variant="outlined"
            sx={{ p: 1.5, gap: 1, display: 'flex', height: '55px', minWidth: '156px' }}
            onClick={() => setUpdateGatewaysModalOpen(true)}
            data-test-id="update-all-gateways-button"
          >
            {t('gateways.update_all_gateways.button')}
          </Button>
        </Box>
      </ContainerHeader>
      <UpdateGatewaysModal open={updateGatewaysModalOpen} handleClose={handleCloseModal} />
      {createGatewayModalOpen && <GatewayCrudDialog mode="create" onClose={handleCloseCreateGateway} />}
      {editingGateway && <GatewayCrudDialog gateway={editingGateway} mode="edit" onClose={handleCloseEditGateway} />}
      {deletingGateway && (
        <GatewayDeleteDialog
          gateway={deletingGateway}
          onClose={handleCloseDeleteGateway}
          onDeleted={handleDeletedGateway}
        />
      )}
      <ContentWrapper
        style={isDesktop ? { paddingLeft: '5rem', paddingRight: '5rem', paddingTop: 0 } : {}}
        className={!isMobile && 'withPageHeader'}
        maxWidth={false}
      >
        <GatewayTable
          rows={gateways}
          onDeleteGateway={setDeletingGateway}
          onEditGateway={setEditingGateway}
          onQueryStateChange={setGatewayListQueryState}
        />
      </ContentWrapper>
    </>
  );
};

Gateways.propTypes = {
  gateways: T.instanceOf(Object).isRequired,
  reloadAction: T.func
};

Gateways.defaultProps = {
  reloadAction: () => null
};

export default Gateways;
